import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Badge } from '../../../components/ui/Badge';
import {
  useMyLoans,
  useLoan,
  useEarlyPayoff,
  useExecuteEarlyPayoff,
  useLoanInstallments,
} from '../../../hooks/useCredit';
import type { LoanStatus, CreditProductType } from '../../../types/credit';

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value / 100);
};

const formatDate = (date: string) => {
  return new Date(date).toLocaleDateString('pt-BR');
};

const statusLabels: Record<LoanStatus, string> = {
  PENDING_ANALYSIS: 'Em Análise',
  PENDING_APPROVAL: 'Aguardando Aprovação',
  APPROVED: 'Aprovado',
  REJECTED: 'Rejeitado',
  PENDING_SIGNATURE: 'Aguardando Assinatura',
  PENDING_DISBURSEMENT: 'Aguardando Desembolso',
  ACTIVE: 'Ativo',
  DEFAULTED: 'Inadimplente',
  PAID_OFF: 'Quitado',
  RENEGOTIATED: 'Renegociado',
  CANCELLED: 'Cancelado',
};

const statusColors: Record<LoanStatus, 'default' | 'primary' | 'success' | 'error'> = {
  PENDING_ANALYSIS: 'default',
  PENDING_APPROVAL: 'default',
  APPROVED: 'primary',
  REJECTED: 'error',
  PENDING_SIGNATURE: 'primary',
  PENDING_DISBURSEMENT: 'primary',
  ACTIVE: 'success',
  DEFAULTED: 'error',
  PAID_OFF: 'success',
  RENEGOTIATED: 'primary',
  CANCELLED: 'default',
};

const productLabels: Record<CreditProductType, string> = {
  PERSONAL_LOAN: 'Empréstimo Pessoal',
  PAYROLL_LOAN: 'Crédito Consignado',
  RECEIVABLES_ADVANCE: 'Antecipação de Recebíveis',
  WORKING_CAPITAL: 'Capital de Giro',
};

export function MyLoans() {
  const [selectedLoanId, setSelectedLoanId] = useState<string | null>(null);
  const [showPayoffModal, setShowPayoffModal] = useState(false);
  const [showInstallments, setShowInstallments] = useState(false);

  const { data: loans, isLoading } = useMyLoans();
  const { data: selectedLoan } = useLoan(selectedLoanId || '', true);
  const { data: installments } = useLoanInstallments(selectedLoanId || '');
  const { data: payoffData } = useEarlyPayoff(selectedLoanId || '');
  const executePayoff = useExecuteEarlyPayoff();

  const handlePayoff = async () => {
    if (!selectedLoanId || !payoffData) return;
    try {
      await executePayoff.mutateAsync({
        loanId: selectedLoanId,
        amount: payoffData.totalAmount,
      });
      setShowPayoffModal(false);
      setSelectedLoanId(null);
    } catch (error) {
      console.error('Erro ao quitar:', error);
    }
  };

  const activeLoans = loans?.filter((l) =>
    ['ACTIVE', 'DEFAULTED', 'PENDING_SIGNATURE', 'PENDING_DISBURSEMENT'].includes(l.status)
  );
  const pendingLoans = loans?.filter((l) =>
    ['PENDING_ANALYSIS', 'PENDING_APPROVAL', 'APPROVED'].includes(l.status)
  );
  const closedLoans = loans?.filter((l) =>
    ['PAID_OFF', 'REJECTED', 'CANCELLED', 'DEFAULTED'].includes(l.status)
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-500">Carregando seus empréstimos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Meus Empréstimos</h1>
        <Button onClick={() => (window.location.href = '/banking/credit/simulate')}>
          Novo Empréstimo
        </Button>
      </div>

      {!loans?.length ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-gray-500 mb-4">
              Você ainda não possui empréstimos.
            </p>
            <Button onClick={() => (window.location.href = '/banking/credit/simulate')}>
              Simular Empréstimo
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Empréstimos Ativos */}
          {activeLoans && activeLoans.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Empréstimos Ativos</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {activeLoans.map((loan) => (
                    <div
                      key={loan.id}
                      className="p-4 border rounded-lg hover:bg-gray-50 cursor-pointer"
                      onClick={() => setSelectedLoanId(loan.id)}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium">{productLabels[loan.productType]}</p>
                          <p className="text-sm text-gray-500">
                            Contratado em {formatDate(loan.createdAt)}
                          </p>
                        </div>
                        <Badge variant={statusColors[loan.status]}>
                          {statusLabels[loan.status]}
                        </Badge>
                      </div>
                      <div className="grid grid-cols-3 gap-4 mt-4">
                        <div>
                          <p className="text-sm text-gray-500">Valor</p>
                          <p className="font-medium">{formatCurrency(loan.amount)}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Parcelas</p>
                          <p className="font-medium">
                            {loan.paidInstallments || 0}/{loan.installments}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Saldo Devedor</p>
                          <p className="font-medium">
                            {formatCurrency(loan.outstandingBalance || loan.amount)}
                          </p>
                        </div>
                      </div>
                      {loan.status === 'DEFAULTED' && (
                        <div className="mt-2 p-2 bg-red-50 rounded text-sm text-red-600">
                          ⚠️ Você possui parcelas em atraso. Regularize para evitar juros adicionais.
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Solicitações Pendentes */}
          {pendingLoans && pendingLoans.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Solicitações em Andamento</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {pendingLoans.map((loan) => (
                    <div
                      key={loan.id}
                      className="p-4 border rounded-lg"
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium">{productLabels[loan.productType]}</p>
                          <p className="text-sm text-gray-500">
                            Solicitado em {formatDate(loan.createdAt)}
                          </p>
                        </div>
                        <Badge variant={statusColors[loan.status]}>
                          {statusLabels[loan.status]}
                        </Badge>
                      </div>
                      <div className="grid grid-cols-2 gap-4 mt-4">
                        <div>
                          <p className="text-sm text-gray-500">Valor Solicitado</p>
                          <p className="font-medium">{formatCurrency(loan.amount)}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Parcelas</p>
                          <p className="font-medium">{loan.installments}x</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Histórico */}
          {closedLoans && closedLoans.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Histórico</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {closedLoans.map((loan) => (
                    <div
                      key={loan.id}
                      className="flex justify-between items-center p-3 border rounded-lg"
                    >
                      <div>
                        <p className="font-medium">{productLabels[loan.productType]}</p>
                        <p className="text-sm text-gray-500">
                          {formatCurrency(loan.amount)} • {loan.installments}x
                        </p>
                      </div>
                      <Badge variant={statusColors[loan.status]}>
                        {statusLabels[loan.status]}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Modal de Detalhes */}
      {selectedLoanId && selectedLoan && !showPayoffModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>Detalhes do Empréstimo</CardTitle>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setSelectedLoanId(null)}
                >
                  ✕
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-500">Status</span>
                <Badge variant={statusColors[selectedLoan.status]}>
                  {statusLabels[selectedLoan.status]}
                </Badge>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Produto</p>
                  <p className="font-medium">{productLabels[selectedLoan.productType]}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Valor Original</p>
                  <p className="font-medium">{formatCurrency(selectedLoan.amount)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Saldo Devedor</p>
                  <p className="font-medium">
                    {formatCurrency(selectedLoan.outstandingBalance || selectedLoan.amount)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Taxa Mensal</p>
                  <p className="font-medium">
                    {(selectedLoan.interestRate * 100).toFixed(2)}%
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">CET (a.a.)</p>
                  <p className="font-medium">{selectedLoan.cet?.toFixed(2)}%</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Parcelas</p>
                  <p className="font-medium">
                    {selectedLoan.paidInstallments || 0}/{selectedLoan.installments}
                  </p>
                </div>
              </div>

              {/* Botões de Ação */}
              <div className="flex gap-2 pt-4 border-t">
                <Button
                  variant="secondary"
                  className="flex-1"
                  onClick={() => setShowInstallments(!showInstallments)}
                >
                  {showInstallments ? 'Ocultar Parcelas' : 'Ver Parcelas'}
                </Button>
                {selectedLoan.status === 'ACTIVE' && (
                  <Button
                    className="flex-1"
                    onClick={() => setShowPayoffModal(true)}
                  >
                    Quitar Antecipado
                  </Button>
                )}
              </div>

              {/* Lista de Parcelas */}
              {showInstallments && installments && (
                <div className="mt-4 max-h-60 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2">#</th>
                        <th className="text-left py-2">Vencimento</th>
                        <th className="text-right py-2">Valor</th>
                        <th className="text-center py-2">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {installments.map((inst: any) => (
                        <tr key={inst.id} className="border-b">
                          <td className="py-2">{inst.number}</td>
                          <td className="py-2">{formatDate(inst.dueDate)}</td>
                          <td className="text-right py-2">
                            {formatCurrency(inst.amount)}
                          </td>
                          <td className="text-center py-2">
                            <Badge
                              variant={
                                inst.status === 'PAID'
                                  ? 'success'
                                  : inst.status === 'OVERDUE'
                                  ? 'error'
                                  : 'default'
                              }
                            >
                              {inst.status === 'PAID'
                                ? 'Pago'
                                : inst.status === 'OVERDUE'
                                ? 'Atrasado'
                                : 'Pendente'}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Modal de Quitação */}
      {showPayoffModal && payoffData && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>Quitação Antecipada</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg text-center">
                <p className="text-sm text-gray-600">Valor para Quitação Hoje</p>
                <p className="text-3xl font-bold text-blue-600">
                  {formatCurrency(payoffData.totalAmount)}
                </p>
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Saldo Devedor:</span>
                  <span>{formatCurrency(payoffData.outstandingPrincipal)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Juros até hoje:</span>
                  <span>{formatCurrency(payoffData.accruedInterest)}</span>
                </div>
                {payoffData.discount > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>Desconto:</span>
                    <span>-{formatCurrency(payoffData.discount)}</span>
                  </div>
                )}
              </div>

              <p className="text-sm text-gray-500 text-center">
                Válido até: {formatDate(payoffData.validUntil)}
              </p>

              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  className="flex-1"
                  onClick={() => setShowPayoffModal(false)}
                >
                  Cancelar
                </Button>
                <Button
                  className="flex-1"
                  onClick={handlePayoff}
                  disabled={executePayoff.isPending}
                >
                  {executePayoff.isPending ? 'Processando...' : 'Pagar Agora'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

export default MyLoans;
